import { ApiClient } from "../ApiClient";

const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;

// Fetch list of providers pending license validation
export async function fetchPendingLicenses() {
  const response = await fetch(
    `${apiBaseUrl}/custom-admin/validate-license/list`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch pending licenses: ${response.status}`);
  }
  return response.json();
}

// Decide license for a given userId. decision: 'Approved' | 'Rejected'
export async function decideLicense(userId, { decision, comment = "" }) {
  if (!userId) throw new Error("userId is required");
  if (!decision || !["Approved", "Rejected"].includes(decision)) {
    throw new Error("decision must be 'Approved' or 'Rejected'");
  }
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(
    `${apiBaseUrl}/custom-admin/validate-license/${userId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: JSON.stringify({ decision: decision, comment }),
    }
  );
  if (!response.ok) {
    const message = `Failed to update license decision: ${response.status}`;
    throw new Error(message);
  }
  return response.json();
}

// Fetch logs with optional filters & pagination
// params: { target_user, action_type, decision, from, to, page, page_size, ordering }
export async function fetchLogs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  const url = qs
    ? `${apiBaseUrl}/custom-admin/logs?${qs}`
    : `${apiBaseUrl}/custom-admin/logs`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch admin logs: ${response.status}`);
  }
  return response.json();
}

// Fetch list of registered CPAs (excluding active and pending validation)
// params: { page, page_size, ordering }
export async function fetchRegisteredCPAs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  const url = qs
    ? `${apiBaseUrl}/custom-admin/registered-cpas/list?${qs}`
    : `${apiBaseUrl}/custom-admin/registered-cpas/list`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch registered CPAs: ${response.status}`);
  }
  return response.json();
}

// Fetch active filings that have not been accepted by any CPA yet
export async function fetchUnacceptedFilings() {
  const response = await fetch(
    `${apiBaseUrl}/custom-admin/unaccepted-filings`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch unaccepted filings: ${response.status}`);
  }
  return response.json();
}

// Notify CPAs about a specific filing that has not been accepted yet
export async function notifyFilingCPAs(filingUuid) {
  if (!filingUuid) throw new Error("filingUuid is required");
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(
    `${apiBaseUrl}/custom-admin/unaccepted-filings`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: JSON.stringify({ filing_uuid: filingUuid }),
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to notify CPAs: ${response.status}`);
  }
  return response.json();
}

// Send reminder to a CPA
export async function sendCPAReminder(userId, { message = "" }) {
  if (!userId) throw new Error("userId is required");
  if (!message || message.trim() === "") {
    throw new Error("message is required");
  }
  const csrfToken = await ApiClient.getCsrfToken();
  const response = await fetch(
    `${apiBaseUrl}/custom-admin/registered-cpas/${userId}/send-reminder`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      credentials: "include",
      body: JSON.stringify({ message: message.trim() }),
    }
  );
  if (!response.ok) {
    const message = `Failed to send reminder: ${response.status}`;
    throw new Error(message);
  }
  return response.json();
}
